/**
 * Utility functions untuk manage shopping cart di localStorage
 */

import { getPhysicalMaxQuantity } from "@/utils/cartStock";

const CART_STORAGE_KEY = "basestore_cart";

function notifyCartUpdated() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event("basestore:cart-updated"));
  } catch {
    // ignore
  }
}

/**
 * Ambil semua items dari cart
 */
export function getCartItems() {
  if (typeof window === "undefined") return [];
  try {
    const items = localStorage.getItem(CART_STORAGE_KEY);
    return items ? JSON.parse(items) : [];
  } catch (error) {
    console.error("Error reading cart from localStorage:", error);
    return [];
  }
}

/**
 * Simpan items ke cart
 */
export function saveCartItems(items) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    notifyCartUpdated();
  } catch (error) {
    console.error("Error saving cart to localStorage:", error);
  }
}

/**
 * Kirim perubahan cart ke API (fire-and-forget) jika user sudah login.
 */
async function syncCartToApi(method, body) {
  if (typeof window === "undefined") return;
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const options = { method, headers };
    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }
    const url =
      method === "DELETE" && body && body.clearAll
        ? "/api/cart/clear"
        : "/api/cart";
    await fetch(url, options);
  } catch (error) {
    console.error("Error syncing cart to API:", error);
  }
}

/**
 * Tambah item ke cart
 * @param {Object} item - Item yang akan ditambahkan
 * @param {number} item.productId - ID produk
 * @param {number} item.variantId - ID variant (optional)
 * @param {number} quantity - Jumlah item
 * @param {Object} productData - Data produk lengkap
 */
export function addToCart(item) {
  const { productId, variantId, variantUuid, quantity = 1, productData, variantData } = item;
  const cartItems = getCartItems();

  // Cek apakah item sudah ada di cart
  // Normalize variantId: null dan undefined dianggap sama
  const normalizedVariantId = variantId || null;
  const existingItemIndex = cartItems.findIndex(
    (cartItem) =>
      cartItem.productId === productId &&
      (cartItem.variantId || null) === normalizedVariantId
  );

  let syncQuantity = quantity;

  if (existingItemIndex >= 0) {
    const row = cartItems[existingItemIndex];
    const prevQty = row.quantity;
    const mergedVariant = row.variantData || variantData || null;
    const nextQty = row.quantity + quantity;
    const maxPhysical = getPhysicalMaxQuantity(
      {
        productData: row.productData,
        variantData: mergedVariant,
        variantUuid: row.variantUuid || variantUuid,
        variantId: row.variantId ?? variantId,
      },
      null,
    );
    row.quantity =
      maxPhysical != null && nextQty > maxPhysical ? maxPhysical : nextQty;
    if (variantData && typeof variantData === "object") {
      row.variantData = { ...mergedVariant, ...variantData };
    }
    // Update variantUuid jika belum ada (item lama)
    if (!row.variantUuid && variantUuid) {
      row.variantUuid = variantUuid;
    }
    syncQuantity = row.quantity - prevQty;
  } else {
    let qty = quantity;
    const maxPhysical = getPhysicalMaxQuantity(
      {
        productData: productData || {},
        variantData: variantData || null,
        variantUuid: variantUuid || null,
        variantId: variantId || null,
      },
      null,
    );
    if (maxPhysical != null && qty > maxPhysical) {
      qty = maxPhysical;
    }
    cartItems.push({
      productId,
      variantId: variantId || null,
      variantUuid: variantUuid || null,
      quantity: qty,
      productData: productData || {},
      variantData: variantData || null,
      addedAt: new Date().toISOString(),
    });
    syncQuantity = qty;
  }

  saveCartItems(cartItems);

  // Sync ke API jika user sudah login
  if (typeof window !== "undefined") {
    const productUuid =
      productData?.uuid || productData?.product_uuid || item.productUuid || null;
    const variantUuid =
      variantData?.uuid || item.variantUuid || null;
    if (productUuid && syncQuantity > 0) {
      syncCartToApi("POST", {
        product_uuid: productUuid,
        ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
        quantity: syncQuantity,
      });
    }
  }
  return cartItems;
}

/**
 * Update quantity item di cart
 */
export function updateCartItemQuantity(productId, variantId, quantity) {
  const cartItems = getCartItems();
  const normalizedVariantId = variantId || null;
  const itemIndex = cartItems.findIndex(
    (item) =>
      item.productId === productId &&
      (item.variantId || null) === normalizedVariantId,
  );

  if (itemIndex >= 0) {
    const target = cartItems[itemIndex];
    if (quantity <= 0) {
      cartItems.splice(itemIndex, 1);
      saveCartItems(cartItems);
      // Hapus di API
      const productUuid =
        target.productData?.uuid ||
        target.productData?.product_uuid ||
        target.productUuid ||
        null;
      const variantUuid =
        target.variantData?.uuid || target.variantUuid || null;
      if (productUuid) {
        syncCartToApi("DELETE", {
          product_uuid: productUuid,
          ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
          quantity: 0,
        });
      }
    } else {
      let qty = quantity;
      const maxPhysical = getPhysicalMaxQuantity(target, null);
      if (maxPhysical != null && qty > maxPhysical) {
        qty = maxPhysical;
      }
      cartItems[itemIndex].quantity = qty;
      saveCartItems(cartItems);
      const productUuid =
        target.productData?.uuid ||
        target.productData?.product_uuid ||
        target.productUuid ||
        null;
      const variantUuid =
        target.variantData?.uuid || target.variantUuid || null;
      if (productUuid) {
        syncCartToApi("PUT", {
          product_uuid: productUuid,
          ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
          quantity,
        });
      }
    }
  }

  return cartItems;
}

/**
 * Hapus item dari cart
 */
export function removeFromCart(productId, variantId) {
  const cartItems = getCartItems();
  const normalizedVariantId = variantId || null;
  const target = cartItems.find(
    (item) =>
      item.productId === productId &&
      (item.variantId || null) === normalizedVariantId,
  );
  const filteredItems = cartItems.filter(
    (item) =>
      !(
        item.productId === productId &&
        (item.variantId || null) === normalizedVariantId
      ),
  );
  saveCartItems(filteredItems);

  if (target) {
    const productUuid =
      target.productData?.uuid ||
      target.productData?.product_uuid ||
      target.productUuid ||
      null;
    const variantUuid =
      target.variantData?.uuid || target.variantUuid || null;
    if (productUuid) {
      syncCartToApi("DELETE", {
        product_uuid: productUuid,
        ...(variantUuid ? { product_variant_uuid: variantUuid } : {}),
        quantity: 0,
      });
    }
  }

  return filteredItems;
}

/**
 * Hapus semua items dari cart
 */
export function clearCart() {
  saveCartItems([]);
  // Clear semua cart di API jika user sudah login
  syncCartToApi("DELETE", { clearAll: true });
  return [];
}

/**
 * Hitung total items di cart
 */
export function getCartTotalItems() {
  const cartItems = getCartItems();
  return cartItems.reduce((total, item) => total + item.quantity, 0);
}

/**
 * Hitung total harga di cart
 */
export function getCartTotalPrice() {
  const cartItems = getCartItems();
  return cartItems.reduce((total, item) => {
    // Diskon hanya per-variant; jika varian ini punya diskon, pakai itu.
    const variantDiscountPrice =
      item.variantData?.discount?.unit_price_discount ??
      item.variantData?.discount_price;

    let effectivePrice;

    if (
      variantDiscountPrice !== undefined &&
      variantDiscountPrice !== null &&
      !isNaN(parseFloat(variantDiscountPrice))
    ) {
      effectivePrice = parseFloat(variantDiscountPrice);
    } else if (item.variantData?.unit_price) {
      // Tidak ada diskon di varian ini → harga normal varian
      effectivePrice = parseFloat(item.variantData.unit_price);
    } else if (item.productData?.unit_price) {
      // Fallback ke harga produk biasa (tanpa diskon global)
      effectivePrice = parseFloat(item.productData.unit_price);
    } else if (item.productData?.price) {
      effectivePrice = parseFloat(item.productData.price);
    } else {
      effectivePrice = 0;
    }

    return total + effectivePrice * item.quantity;
  }, 0);
}

